import { useState, useEffect } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Add as AddIcon, Business as BusinessIcon } from '@mui/icons-material';
import { useContextStore } from '../../store/contextStore';

/**
 * Tenant switcher component for selecting the current tenant.
 */
export const TenantSwitcher = () => {
  const { currentTenant, availableTenants, setTenant, loadTenants, isLoading } = useContextStore();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Load tenants on mount
    if (availableTenants.length === 0) {
      loadTenants();
    }
  }, [availableTenants.length, loadTenants]);

  // Keyboard shortcut: Ctrl+T
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 't') {
        e.preventDefault();
        setOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const tenantId = event.target.value;
    if (tenantId === 'create') {
      // TODO: Open create tenant dialog
      return;
    }

    const selectedTenant = availableTenants.find((t) => t.id === tenantId);
    if (selectedTenant) {
      setTenant(selectedTenant);
    }
  };

  const displayValue = currentTenant?.name || 'Select Tenant';

  return (
    <Box sx={{ minWidth: 200, display: 'flex', alignItems: 'center', gap: 1 }}>
      <BusinessIcon sx={{ color: 'text.secondary' }} />
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="tenant-select-label">Tenant</InputLabel>
        <Select
          labelId="tenant-select-label"
          id="tenant-select"
          value={currentTenant?.id || ''}
          label="Tenant"
          onChange={handleChange}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          disabled={isLoading || availableTenants.length === 0}
          renderValue={(value) => {
            const tenant = availableTenants.find((t) => t.id === value);
            if (!tenant) return displayValue;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">{tenant.name}</Typography>
                {!tenant.isActive && (
                  <Chip label="Inactive" size="small" color="error" sx={{ height: 20 }} />
                )}
              </Box>
            );
          }}
        >
          {availableTenants.map((tenant) => (
            <MenuItem key={tenant.id} value={tenant.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography variant="body2">{tenant.name}</Typography>
                {!tenant.isActive && (
                  <Chip label="Inactive" size="small" color="error" sx={{ height: 20, ml: 1 }} />
                )}
              </Box>
            </MenuItem>
          ))}
          <MenuItem value="create" disabled>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon fontSize="small" />
              <Typography variant="body2">Create New Tenant</Typography>
            </Box>
          </MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};
